import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Avatar as MuiAvatar } from '@mui/material';
import { Phone, CallEnd } from '@mui/icons-material';
import { useSocket } from '../../context/SocketContext';
import { useAppSelector } from '../../store/hooks';

// An animated overlay for incoming calls
const IncomingCall: React.FC = () => {
  const { socket } = useSocket();
  const user = useAppSelector((s) => s.auth.user);
  
  const [incomingCall, setIncomingCall] = useState<any>(null);

  useEffect(() => {
    if (!socket) return;

    socket.on('incoming-call', (data: any) => {
      setIncomingCall(data);
      // Play a ringing sound here if we had one
    });

    socket.on('call-ended', () => {
      setIncomingCall(null);
    });
    
    // Automatically hide if caller misses the call (caller timeout)
    // We could listen to a 'missed-call' event from backend but we can also just timeout
    // Actually, backend doesn't emit missed-call to receiver explicitly if it's already creating a notification.
    // The caller might emit 'end-call' when they timeout.

    return () => {
      socket.off('incoming-call');
      socket.off('call-ended');
    };
  }, [socket]);

  const handleAccept = () => {
    if (!socket || !incomingCall) return;
    socket.emit('accept-call', { callerId: incomingCall.caller._id, receiverId: user?._id });
    // In a real app, we'd navigate to a Call View or open a video overlay.
    // Here we'll just alert and hide for the mockup
    alert(`Call Connected with ${incomingCall.caller.name}!`);
    setIncomingCall(null);
  };

  const handleDecline = () => {
    if (!socket || !incomingCall) return;
    socket.emit('reject-call', { callerId: incomingCall.caller._id, receiverId: user?._id });
    setIncomingCall(null);
  };

  if (!incomingCall) return null;

  return (
    <Box sx={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <Box sx={{
        width: 320, background: '#fff', borderRadius: 4, p: 4, textAlign: 'center',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        animation: 'slideDown 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
      }}>
        <style>
          {`
            @keyframes slideDown {
              0% { transform: translateY(-50px); opacity: 0; }
              100% { transform: translateY(0); opacity: 1; }
            }
            @keyframes ring {
              0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
              70% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(34, 197, 94, 0); }
              100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
            }
          `}
        </style>
        
        <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
          <MuiAvatar 
            sx={{ width: 80, height: 80, background: incomingCall.caller.color, fontSize: 28, mx: 'auto' }}
          >
            {incomingCall.caller.initials}
          </MuiAvatar>
        </Box>

        <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 0.5 }}>{incomingCall.caller.name}</Typography>
        <Typography sx={{ fontSize: 14, color: '#64748b', mb: 4 }}>
          Incoming {incomingCall.type === 'video' ? 'Video' : 'Voice'} Call...
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={handleDecline}
              sx={{ 
                width: 56, height: 56, background: '#ef4444', color: '#fff',
                '&:hover': { background: '#dc2626' }
              }}
            >
              <CallEnd />
            </IconButton>
            <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Decline</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={handleAccept}
              sx={{ 
                width: 56, height: 56, background: '#22c55e', color: '#fff',
                animation: 'ring 1.5s infinite ease-in-out',
                '&:hover': { background: '#16a34a' }
              }}
            >
              <Phone />
            </IconButton>
            <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Accept</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default IncomingCall;
